/**
 * Opdracht-mix — rapportage over de Gripp "vrije velden" (Doel, Projecttype,
 * Dienst, Omvang), bijgehouden sinds ~Q2 2026. Laat zien wat voor werk we doen
 * (aantallen én omzet), hoe de mix per maand verschuift, en welke projecten nog
 * geen typering hebben. De ↑↓-delta's vergelijken de laatste 90 dagen met de
 * periode ervoor — zo zie je of de mix beweegt richting de nieuwe koers.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Paper, CircularProgress, Alert, ToggleButtonGroup, ToggleButton,
  Chip, Stack, Tooltip, FormControlLabel, Switch, Button, Dialog, DialogTitle, DialogContent, DialogActions, Checkbox,
} from '@mui/material';
import toast from 'react-hot-toast';
import { bevestig } from '../../utils/bevestig';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip as ReTooltip, Legend, ResponsiveContainer, CartesianGrid } from 'recharts';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { buildGrippProjectUrl } from '../../utils/grippDeepLink';
import PageHeader from '../../ui-components/PageHeader';
import SnelNalopen from './SnelNalopen';

const DIMENSIONS = [
  { key: 'doel', label: 'Doel', icon: '🎯' },
  { key: 'projecttype', label: 'Projecttype', icon: '🎬' },
  { key: 'dienst', label: 'Dienst', icon: '🛠️' },
  { key: 'omvang', label: 'Omvang (uit bedrag)', icon: '📏' },
  { key: 'contractvorm', label: 'Contractvorm', icon: '📃' },
  { key: 'branche', label: 'Branche', icon: '🏢' },
];

// Vaste sortering voor omvang; overige dimensies sorteren op grootte.
const OMVANG_ORDER = ['XS', 'S', 'M', 'L', 'XL'];

// Nieuwe koers (Onepager Herpositionering, mei 2026): van projectleverancier naar
// strategische communicatieadviseur. Drie hoofdpijlers moeten groeien; mapping van
// Gripp doel-waarden → pijler. Onbekende doelen vallen onder "Overig (neven/los)".
// Definities doelcategorieën v1.0 (19 sep 2026): vier doelen, drie hoofdpijlers; 'Marketing en merkcontent' is geen pijler.
// Vervallen waarden (Branding, Sales, Training) tellen NIET meer als pijler — die worden per project omgezet.
const PIJLERS = [
  { key: 'ic', label: 'Interne communicatie', kleur: '#526075', doelen: ['interne communicatie'] },
  { key: 'amc', label: 'Arbeidsmarktcommunicatie', kleur: '#0e7490', doelen: ['recruitment', 'arbeidsmarkt communicatie'] },
  { key: 'corp', label: 'Corporate en publiekscommunicatie', kleur: '#635983', doelen: ['corporate'] },
];
// Spaties negeren: Gripp heet het sinds 15 sep 2026 'Arbeidsmarkt communicatie' (met spatie),
// de pijler stond op 'arbeidsmarktcommunicatie' — anders viel de hele pijler onder Overig.
const _kaal = (t) => String(t || '').toLowerCase().replace(/\s+/g, '');
const pijlerVoorDoel = (doel) => {
  const d = _kaal(doel);
  return PIJLERS.find((p) => p.doelen.some((x) => d.includes(_kaal(x))))?.key || 'overig';
};

const MIN_RECENT = 15;   // minimum aantal projecten voor de 90-dagenvergelijking (feedback 19 sep)
const mediaan = (xs) => { const a = [...xs].sort((x, y) => x - y); return a.length ? (a.length % 2 ? a[(a.length - 1) / 2] : (a[a.length / 2 - 1] + a[a.length / 2]) / 2) : 0; };
const isAdvies = (p) => /concept & strategie|diagnose/i.test(p.dienst || '') || /diagnose/i.test(p.projecttype || '');
const isDiagnose = (p) => /diagnose/i.test(p.dienst || '') || /diagnose/i.test(p.projecttype || '');

const DOELEN_GELDIG = ['Interne communicatie', 'Arbeidsmarkt communicatie', 'Corporate en publiekscommunicatie', 'Marketing en merkcontent'];

const PALETTE = ['#526075', '#635983', '#0e7490', '#b45309', '#15803d', '#9d174d', '#4338ca', '#a16207', '#0f766e', '#7c2d12'];

const euro = (v) => `€ ${Math.round(v).toLocaleString('nl-NL')}`;
const pct = (v, total) => (total > 0 ? Math.round((v / total) * 100) : 0);

function monthKey(iso) {
  return iso ? String(iso).slice(0, 7) : null; // YYYY-MM
}

// Verdeling van projecten over de waarden van één dimensie.
function distribution(projects, dim, basis) {
  const map = new Map();
  for (const p of projects) {
    const val = p[dim] || (dim === 'branche' ? 'Nog niet ingedeeld' : 'Onbekend');
    const cur = map.get(val) || { value: val, count: 0, omzet: 0 };
    cur.count += 1;
    cur.omzet += p.amount_excl_vat || 0;
    map.set(val, cur);
  }
  const rows = [...map.values()];
  if (dim === 'omvang') {
    rows.sort((a, b) => (OMVANG_ORDER.indexOf(a.value) + 99) - (OMVANG_ORDER.indexOf(b.value) + 99)
      || OMVANG_ORDER.indexOf(a.value) - OMVANG_ORDER.indexOf(b.value));
  } else {
    rows.sort((a, b) => (basis === 'omzet' ? b.omzet - a.omzet : b.count - a.count));
  }
  return rows;
}

function DimensionCard({ dim, label, icon, projects, recent, basis, onRij }) {
  const rows = distribution(projects, dim, basis);
  const total = rows.reduce((s, r) => s + (basis === 'omzet' ? r.omzet : r.count), 0);
  const recentRows = distribution(recent, dim, basis);
  const recentTotal = recentRows.reduce((s, r) => s + (basis === 'omzet' ? r.omzet : r.count), 0);
  const recentShare = new Map(recentRows.map((r) => [r.value, pct(basis === 'omzet' ? r.omzet : r.count, recentTotal)]));

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Typography sx={{ fontWeight: 800, fontSize: 15, mb: 1.5 }}>{icon} {label}</Typography>
      <Stack spacing={1}>
        {rows.map((r, i) => {
          const val = basis === 'omzet' ? r.omzet : r.count;
          const share = pct(val, total);
          const rShare = recentShare.get(r.value);
          // Richting zegt in een verdelingskaart niets over goed of slecht → grijs; en pas tonen bij genoeg projecten.
          const delta = rShare != null && recent.length >= MIN_RECENT ? rShare - share : null;
          const isOnbekend = r.value === 'Onbekend' || r.value === 'Nog niet ingedeeld';
          return (
            <Box key={r.value} onClick={() => onRij?.(dim, label, r.value)} title="Klik voor de klanten achter dit getal"
              sx={{ cursor: onRij ? 'pointer' : 'default', borderRadius: 1, px: 0.5, mx: -0.5, '&:hover': onRij ? { bgcolor: '#f8fafc' } : {} }}>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.25 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: isOnbekend ? '#94a3b8' : '#2d3335' }}>
                  {r.value}
                  {delta != null && Math.abs(delta) >= 3 && !isOnbekend && (
                    <Tooltip title={`Laatste 90 dagen: ${rShare}% (was ${share}% over de hele periode)`}>
                      <Box component="span" sx={{ ml: 0.75, fontSize: 11, fontWeight: 700, color: '#94a3b8', cursor: 'help' }}>
                        {delta > 0 ? '↑' : '↓'}{Math.abs(delta)}%
                      </Box>
                    </Tooltip>
                  )}
                </Typography>
                <Typography sx={{ fontSize: 12, color: '#767c7e' }}>
                  {basis === 'omzet' ? euro(r.omzet) : `${r.count}×`} · {share}%
                </Typography>
              </Stack>
              <Box sx={{ height: 6, borderRadius: 3, bgcolor: '#f1f4f5', overflow: 'hidden' }}>
                <Box sx={{ width: `${share}%`, height: '100%', borderRadius: 3, bgcolor: isOnbekend ? '#cbd5e1' : PALETTE[i % PALETTE.length] }} />
              </Box>
            </Box>
          );
        })}
        {rows.length === 0 && <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>Geen data</Typography>}
      </Stack>
    </Paper>
  );
}

export default function OpdrachtMix() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [basis, setBasis] = useState('count');       // count | omzet
  const [trendDim, setTrendDim] = useState('projecttype');
  const [chartType, setChartType] = useState('bar'); // bar | line
  // Series aan/uit via legenda-klik (per dimensie onthouden zou overkill zijn).
  const [hiddenSeries, setHiddenSeries] = useState({});
  const toggleSeries = (name) => setHiddenSeries((prev) => ({ ...prev, [name]: !prev[name] }));
  const [alleenGetypeerd, setAlleenGetypeerd] = useState(true);
  // Periode-zoom: standaard dit jaar — de vrije velden worden pas sinds 2026 breed
  // ingevuld en oude jaren vervuilen de mix.
  const currentYear = new Date().getFullYear();
  const [periode, setPeriode] = useState(String(currentYear)); // '12m' | jaar | 'alles'
  // ── Typering-check (AI toetst doel/type/dienst/omvang; Joris 19 sep) ──
  const [segment, setSegment] = useState('alle');           // alle | exclusive | direct
  const [portfolio, setPortfolio] = useState('alle');       // alle | energie | bureaus | publiek | industrie
  const [portfolios, setPortfolios] = useState([]);
  const [tcBusy, setTcBusy] = useState(false);
  const [tcStap, setTcStap] = useState('');
  const [tcResult, setTcResult] = useState(null);
  const [tcOpen, setTcOpen] = useState(false);
  const [tcKeuze, setTcKeuze] = useState({});
  const [tcApplying, setTcApplying] = useState(false);
  const [pdfBezig, setPdfBezig] = useState(false);
  const [nalopenOpen, setNalopenOpen] = useState(false);
  const [nalopenFilter, setNalopenFilter] = useState(null);   // 'doel' = alleen leeg/vervallen doel
  const [klantenDialog, setKlantenDialog] = useState(null);   // { dim, label, value }
  const laadLaatsteCheck = async (open = true) => {
    const r = await authFetch(`${BACKEND_URL}/Api/gripp/typering-check/laatste`);
    const d = await r.json().catch(() => ({}));
    if (!r.ok || !d.check_id) { if (open) toast('Nog geen controle gedraaid.'); return null; }
    setTcResult(d);
    setTcKeuze(Object.fromEntries((d.items || []).filter((i) => (i.zekerheid || 0) >= 0.7).map((i) => [i.gripp_id, true])));
    if (open) setTcOpen(true);
    return d;
  };
  const startTyperingCheck = async () => {
    setTcBusy(true); setTcStap('starten…');
    try {
      const vanaf = periode === 'alles' ? '2025-01-01' : (periode === '12m' ? new Date(Date.now() - 365 * 86400000).toISOString().slice(0, 10) : `${periode}-01-01`);
      const r = await authFetch(`${BACKEND_URL}/Api/gripp/typering-check/start?vanaf=${vanaf}`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || d.detail || 'Starten mislukt');
      if (!d.gestart && d.reden) toast(d.reden);
      for (let i = 0; i < 240; i += 1) {
        await new Promise((res) => setTimeout(res, 5000)); // eslint-disable-line no-await-in-loop
        const s = await authFetch(`${BACKEND_URL}/Api/gripp/typering-check/status`).then((x) => x.json()).catch(() => null); // eslint-disable-line no-await-in-loop
        if (!s) continue;
        setTcStap(s.stap || (s.bezig ? 'bezig…' : ''));
        if (!s.bezig) {
          if (s.laatste?.status === 'mislukt') throw new Error(`Controle mislukt: ${s.laatste.fout || ''}`);
          break;
        }
      }
      await laadLaatsteCheck(true);
    } catch (e) { toast.error(e.message); }
    finally { setTcBusy(false); setTcStap(''); }
  };
  const pasTyperingToe = async () => {
    const items = (tcResult?.items || []).filter((i) => tcKeuze[i.gripp_id])
      .map((i) => ({ gripp_id: i.gripp_id, velden: Object.fromEntries(i.afwijkingen.map((a) => [a.veld, a.voorstel])) }));
    if (!items.length) return;
    if (!(await bevestig({ titel: `${items.length} projecten bijwerken in Gripp?`, tekst: 'De vrije velden (doel, projecttype, dienst, omvang) worden in Gripp overschreven met het aangevinkte voorstel. Dit is niet automatisch terug te draaien.', knop: 'Bijwerken' }))) return;
    setTcApplying(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/gripp/typering-check/toepassen`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || d.detail || 'Bijwerken mislukt');
      toast.success(`${d.gedaan} projecten bijgewerkt in Gripp${(d.fouten || []).length ? ` · ${d.fouten.length} mislukt` : ''}`);
      // Lokaal doorvoeren zodat de mix meteen klopt, en de bijgewerkte regels uit de lijst halen.
      const perId = Object.fromEntries(items.map((i) => [i.gripp_id, i.velden]));
      setData((prev) => (prev || []).map((p) => (perId[p.gripp_id] ? { ...p, ...perId[p.gripp_id] } : p)));
      setTcResult((prev) => prev ? { ...prev, items: (prev.items || []).filter((i) => !perId[i.gripp_id]) } : prev);
      setTcKeuze({});
    } catch (e) { toast.error(e.message); }
    finally { setTcApplying(false); }
  };

  useEffect(() => {
    let active = true;
    authFetch(`${BACKEND_URL}/Api/gripp/project-mix`)
      .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => { if (!active) return; if (!ok) throw new Error(d.error || 'Laden mislukt'); setData(d.projects || []); setPortfolios(d.portfolios || []); })
      .catch((e) => { if (active) setError(e.message); });
    return () => { active = false; };
  }, []);

  const { scoped, scopedAlle, typed, untyped, recent, months, deltaZinvol } = useMemo(() => {
    const all = data || [];
    // "Getypeerd" = volgens de 2026-systematiek: Doel/Dienst/Omvang/Contractvorm.
    // Projecttype telt bewust NIET mee — dat veld wordt al sinds 2017 gevuld,
    // waardoor vrijwel elk oud project anders als "getypeerd" zou gelden.
    const hasType = (p) => p.doel || p.dienst || p.contractvorm;   // omvang telt niet mee: die wordt nu uit het bedrag berekend

    // Periode-filter op startdatum.
    const inPeriode = (p) => {
      const d = p.start_date || '';
      if (periode === 'alles') return true;
      if (periode === '12m') return d >= new Date(Date.now() - 365 * 86400000).toISOString().slice(0, 10);
      return d.slice(0, 4) === periode; // jaartal
    };
    const scopedAlle = all.filter(inPeriode);
    // Segment: Very Video (reclamebureaus) apart kunnen bekijken, of juist zonder.
    const inSegment = (p) => (segment === 'alle' || (segment === 'exclusive' ? !!p.exclusive : !p.exclusive)) && (portfolio === 'alle' || p.portfolio === portfolio);
    const scoped = scopedAlle.filter(inSegment);

    const typed = scoped.filter(hasType);
    // "Nog typeren": alleen EXTERNE 2026-projecten — oude/interne Full Frame-projecten
    // (Camera testen, inloggegevens, …) hoeven niet met terugwerkende kracht getypeerd.
    const intern = (p) => /full frame|gripp intern/i.test(p.company || '');
    const untyped = all.filter((p) => !hasType(p) && !intern(p) && (p.start_date || '') >= '2026-01-01');

    // 90-dagen-delta is alleen zinvol als de periode t/m vandaag loopt.
    const deltaZinvol = periode === 'alles' || periode === '12m' || periode === String(currentYear);
    const cutoff = new Date(Date.now() - 90 * 86400000).toISOString().slice(0, 10);
    const recent = deltaZinvol ? typed.filter((p) => (p.start_date || '') >= cutoff) : [];

    // Trend: per maand binnen de periode.
    const monthMap = new Map();
    for (const p of typed) {
      const m = monthKey(p.start_date);
      if (!m) continue;
      if (!monthMap.has(m)) monthMap.set(m, []);
      monthMap.get(m).push(p);
    }
    const months = [...monthMap.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    return { scoped, scopedAlle, typed, untyped, recent, months, deltaZinvol };
  }, [data, periode, currentYear, segment, portfolio]);

  const projects = alleenGetypeerd ? typed : scoped;

  const trendData = useMemo(() => {
    const values = [...new Set(typed.map((p) => p[trendDim] || 'Onbekend'))];
    return {
      values,
      rows: months.map(([m, ps]) => {
        const row = { maand: m.slice(2) };
        for (const v of values) {
          const sub = ps.filter((p) => (p[trendDim] || 'Onbekend') === v);
          row[v] = basis === 'omzet'
            ? Math.round(sub.reduce((s, p) => s + (p.amount_excl_vat || 0), 0))
            : sub.length;
        }
        return row;
      }),
    };
  }, [months, typed, trendDim, basis]);

  const totalOmzet = projects.reduce((s, p) => s + (p.amount_excl_vat || 0), 0);

  return (
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
      <PageHeader
        icon="donut_small"
        title="Opdracht-mix"
        subtitle="Wat voor werk doen we? Doel, projecttype, dienst en omvang uit Gripp — met de verschuiving richting de nieuwe koers (↑↓ = laatste 90 dagen t.o.v. de hele periode)."
        rightActions={(
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <FormControlLabel
              sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 12, fontWeight: 600, color: '#556071' } }}
              control={<Switch size="small" checked={alleenGetypeerd} onChange={(e) => setAlleenGetypeerd(e.target.checked)} />}
              label="Alleen getypeerd (2026-velden)"
            />
            <ToggleButtonGroup size="small" exclusive value={periode} onChange={(_e, v) => v && setPeriode(v)}
              sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 1.25, fontSize: 12, textTransform: 'none', fontWeight: 700 } }}>
              <ToggleButton value={String(currentYear)}>{currentYear}</ToggleButton>
              <ToggleButton value="12m">12 mnd</ToggleButton>
              <ToggleButton value={String(currentYear - 1)}>{currentYear - 1}</ToggleButton>
              <ToggleButton value="alles">Alles</ToggleButton>
            </ToggleButtonGroup>
            <ToggleButtonGroup size="small" exclusive value={basis} onChange={(_e, v) => v && setBasis(v)}
              sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 1.5, fontSize: 12, textTransform: 'none', fontWeight: 700 } }}>
              <ToggleButton value="count">Aantal</ToggleButton>
              <ToggleButton value="omzet">Omzet</ToggleButton>
            </ToggleButtonGroup>
            <ToggleButtonGroup size="small" exclusive value={segment} onChange={(_e, v) => v && setSegment(v)}
              sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 1.25, fontSize: 12, textTransform: 'none', fontWeight: 700 } }}>
              <ToggleButton value="alle">Alle klanten</ToggleButton>
              <ToggleButton value="exclusive">Very Video</ToggleButton>
              <ToggleButton value="direct">Direct</ToggleButton>
            </ToggleButtonGroup>
            <ToggleButtonGroup size="small" exclusive value={portfolio} onChange={(_e, v) => v && setPortfolio(v)}
              sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 1.25, fontSize: 12, textTransform: 'none', fontWeight: 700 } }}>
              <ToggleButton value="alle">Alle portfolio's</ToggleButton>
              {portfolios.map((pf) => <ToggleButton key={pf.key} value={pf.key}>{pf.label.replace(' (Very Video)', '')}</ToggleButton>)}
            </ToggleButtonGroup>
          </Stack>
        )}
      />

      {/* Werkbalk met acties — los van de kop, zodat hij netjes doorloopt op smallere schermen */}
      <Paper variant="outlined" sx={{ p: 1.25, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', mr: 0.5 }}>Acties</Typography>
                <Tooltip title="AI leest per project naam, klant, offerteregels en bedrag en toetst of doel, projecttype, dienst en omvang kloppen. Je krijgt de afwijkingen voorgelegd; niets wordt vanzelf aangepast.">
                  <span>
                    <Button size="small" variant="outlined" disabled={tcBusy} onClick={startTyperingCheck} sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {tcBusy ? `🔍 ${tcStap || 'bezig…'}` : '🔍 Typering controleren (AI)'}
                    </Button>
                  </span>
                </Tooltip>
                <Button size="small" onClick={() => laadLaatsteCheck(true)} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>Laatste resultaat</Button>
                <Tooltip title="Eén project per kaart: Enter = klopt, velden aanpassen als het niet klopt, → volgende. Alle projecten van 2026; wijzigingen gaan direct naar Gripp.">
                  <span>
                    <Button size="small" variant="contained" disableElevation onClick={() => { setNalopenFilter(null); setNalopenOpen(true); }} sx={{ textTransform: 'none', fontWeight: 800, whiteSpace: 'nowrap', bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' } }}>⚡ Snel nalopen</Button>
                  </span>
                </Tooltip>
                <Tooltip title="Compacte PDF met kerncijfers, branche, Very Video, alle dimensies en de projectlijst — voor analyse in een LLM. Volgt de gekozen periode en het segment.">
                  <span>
                    <Button size="small" variant="outlined" disabled={pdfBezig} onClick={async () => {
                      setPdfBezig(true);
                      try {
                        const r = await authFetch(`${BACKEND_URL}/Api/gripp/project-mix/export.pdf?periode=${encodeURIComponent(periode)}&segment=${segment}`);
                        if (!r.ok) throw new Error(`PDF maken mislukt (HTTP ${r.status})`);
                        const url = URL.createObjectURL(await r.blob());
                        const a = document.createElement('a'); a.href = url; a.download = `opdracht-mix-${periode}-${segment}.pdf`; document.body.appendChild(a); a.click(); a.remove();
                        setTimeout(() => URL.revokeObjectURL(url), 5000);
                      } catch (e) { toast.error(e.message); }
                      finally { setPdfBezig(false); }
                    }} sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}>{pdfBezig ? 'PDF maken…' : '⬇ PDF'}</Button>
                  </span>
                </Tooltip>
                <Tooltip title="Deelt klanten zonder branche in met AI (bedrijfsnaam + projectnamen). Loopt op de achtergrond; herlaad de pagina na een minuut. Handmatige indelingen blijven staan.">
                  <span>
                    <Button size="small" onClick={async () => {
                      const r = await authFetch(`${BACKEND_URL}/Api/gripp/branches/classificeer`, { method: 'POST' });
                      const d = await r.json().catch(() => ({}));
                      toast(r.ok ? (d.gestart ? 'Branche-indeling gestart — herlaad over een minuut.' : 'Indeling loopt al.') : (d.error || 'Starten mislukt'));
                    }} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>🏢 Branches indelen</Button>
                  </span>
                </Tooltip>
        {(() => {
          const probleem = (data || []).filter((p) => (p.start_date || '') >= '2026-01-01' && !/full frame|gripp intern/i.test(p.company || '') && (!p.doel || !DOELEN_GELDIG.includes(p.doel))).length;
          return probleem > 0 ? (
            <Tooltip title="Opent Snel nalopen met alleen de projecten waarvan het doel leeg is of een vervallen waarde heeft.">
              <Button size="small" variant="outlined" color="warning" onClick={() => { setNalopenFilter('doel'); setNalopenOpen(true); }} sx={{ textTransform: 'none', fontWeight: 800, whiteSpace: 'nowrap' }}>
                🎯 Doel corrigeren ({probleem})
              </Button>
            </Tooltip>
          ) : null;
        })()}
      </Paper>

      <SnelNalopen open={nalopenOpen} startFilter={nalopenFilter} onClose={() => { setNalopenOpen(false); setNalopenFilter(null); }} onGewijzigd={(w) => {
        setData((prev) => (prev || []).map((p) => {
          let q = p;
          if (p.gripp_id === w.gripp_id) q = { ...q, ...w.velden, ...(w.eindklant !== undefined ? { eindklant: w.eindklant } : {}) };
          if (w.branche && w.company_id && p.company_id === w.company_id) q = { ...q, branche: w.branche };
          return q;
        }));
      }} />

      {/* Klanten achter één waarde van een dimensie */}
      <Dialog open={!!klantenDialog} onClose={() => setKlantenDialog(null)} maxWidth="sm" fullWidth>
        {klantenDialog && (() => {
          const leeg = klantenDialog.dim === 'branche' ? 'Nog niet ingedeeld' : 'Onbekend';
          const sel = projects.filter((p) => (p[klantenDialog.dim] || leeg) === klantenDialog.value);
          const per = new Map();
          for (const p of sel) {
            const k = p.company || 'geen klant';
            const cur = per.get(k) || { klant: k, n: 0, omzet: 0, projecten: [] };
            cur.n += 1; cur.omzet += p.amount_excl_vat || 0; cur.projecten.push(p); per.set(k, cur);
          }
          const rows = [...per.values()].sort((a, b) => (basis === 'omzet' ? b.omzet - a.omzet : b.n - a.n));
          const tot = sel.reduce((s, p) => s + (p.amount_excl_vat || 0), 0);
          return (
            <>
              <DialogTitle sx={{ fontWeight: 800 }}>{klantenDialog.label}: {klantenDialog.value}
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1.5 }}>{sel.length} projecten · {euro(tot)} · {rows.length} klanten</Typography>
              </DialogTitle>
              <DialogContent dividers>
                <Stack spacing={1}>
                  {rows.map((r) => (
                    <Box key={r.klant}>
                      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                        <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>{r.klant}</Typography>
                        <Typography sx={{ fontSize: 12, color: '#64748b' }}>{r.n}× · {euro(r.omzet)}</Typography>
                      </Stack>
                      {r.projecten.slice(0, 6).map((p) => {
                        const u = buildGrippProjectUrl(p.gripp_id, p.company_id);
                        return (
                          <Typography key={p.gripp_id} sx={{ fontSize: 12, color: '#475569', pl: 1 }}>
                            {u ? <a href={u} target="_blank" rel="noopener noreferrer" style={{ color: '#3b5bdb', textDecoration: 'none' }}>{p.number}</a> : p.number} {p.name} · {euro(p.amount_excl_vat || 0)}
                          </Typography>
                        );
                      })}
                      {r.projecten.length > 6 && <Typography sx={{ fontSize: 11.5, color: '#94a3b8', pl: 1 }}>… en {r.projecten.length - 6} meer</Typography>}
                    </Box>
                  ))}
                </Stack>
              </DialogContent>
              <DialogActions><Button onClick={() => setKlantenDialog(null)} sx={{ textTransform: 'none' }}>Sluiten</Button></DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* Typering-check: AI-voorstellen per project, PL vinkt aan wat naar Gripp mag */}
      <Dialog open={tcOpen} onClose={() => setTcOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          🔍 Typering-check
          {tcResult?.samenvatting && (
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1.5 }}>
              {tcResult.samenvatting.projecten_getoetst} projecten getoetst · {tcResult.samenvatting.met_afwijking} met een voorstel ({tcResult.samenvatting.aanvullingen} aanvullingen, {tcResult.samenvatting.wijzigingen} wijzigingen)
              {tcResult.klaar ? ` · ${String(tcResult.klaar).slice(0, 16).replace('T', ' ')}` : ''}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {!tcResult && <Typography color="text.secondary">Nog geen resultaat. Start de controle met de knop bovenin.</Typography>}
          {tcResult && (tcResult.items || []).length === 0 && <Alert severity="success">Geen afwijkingen gevonden.</Alert>}
          {(tcResult?.fouten || []).length > 0 && <Alert severity="warning" sx={{ mb: 1.5 }}>{tcResult.fouten.length} batch(es) mislukt; de rest is wel getoetst.</Alert>}
          <Stack spacing={1}>
            {(tcResult?.items || []).map((it) => {
              const url = buildGrippProjectUrl(it.gripp_id, it.company_id);
              const aan = !!tcKeuze[it.gripp_id];
              return (
                <Box key={it.gripp_id} sx={{ p: 1.25, borderRadius: 1.5, border: '1px solid', borderColor: aan ? '#c7d2fe' : '#e2e8f0', bgcolor: aan ? '#f5f7ff' : '#fff' }}>
                  <Stack direction="row" alignItems="flex-start" spacing={0.5}>
                    <Checkbox size="small" checked={aan} onChange={(e) => setTcKeuze((p) => ({ ...p, [it.gripp_id]: e.target.checked }))} sx={{ p: 0.5, mt: -0.25 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>
                        {url ? <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#3b5bdb', textDecoration: 'none' }}>{it.number} — {it.name} ↗</a> : `${it.number} — ${it.name}`}
                        <Box component="span" sx={{ ml: 1, fontWeight: 400, color: '#64748b' }}>{it.company}{it.exclusive ? ` · Very Video (${it.exclusive})` : ''} · {euro(it.amount || 0)}</Box>
                      </Typography>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                        {it.afwijkingen.map((a) => (
                          <Chip key={a.veld} size="small" label={`${a.veld}: ${a.huidig || '—'} → ${a.voorstel}`}
                            sx={{ height: 22, fontSize: 11.5, fontWeight: 600, bgcolor: a.soort === 'aanvulling' ? '#ecfdf5' : '#fef3c7', color: a.soort === 'aanvulling' ? '#15803d' : '#92400e' }} />
                        ))}
                        <Chip size="small" label={`zekerheid ${Math.round((it.zekerheid || 0) * 100)}%`} sx={{ height: 22, fontSize: 11, bgcolor: '#f1f5f9', color: '#475569' }} />
                      </Stack>
                      {it.reden && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{it.reden}</Typography>}
                    </Box>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTcKeuze(Object.fromEntries((tcResult?.items || []).map((i) => [i.gripp_id, true])))} sx={{ textTransform: 'none' }}>Alles</Button>
          <Button onClick={() => setTcKeuze({})} sx={{ textTransform: 'none' }}>Niets</Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setTcOpen(false)} sx={{ textTransform: 'none' }}>Sluiten</Button>
          <Button variant="contained" disableElevation disabled={tcApplying || !Object.values(tcKeuze).some(Boolean)} onClick={pasTyperingToe} sx={{ textTransform: 'none', fontWeight: 700 }}>
            {tcApplying ? 'Bezig…' : `${Object.values(tcKeuze).filter(Boolean).length} projecten bijwerken in Gripp`}
          </Button>
        </DialogActions>
      </Dialog>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!data && !error && <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>}

      {data && (
        <>
          {/* Kerncijfers */}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
            <Chip label={`${projects.length} projecten`} sx={{ fontWeight: 700 }} />
            <Chip label={`${euro(totalOmzet)} excl. btw`} sx={{ fontWeight: 700 }} />
            <Chip label={`${typed.length} getypeerd`} sx={{ fontWeight: 700, bgcolor: '#ecfdf5', color: '#15803d' }} />
            {(() => {
              const zonderCv = scoped.filter((p) => !p.contractvorm && !/full frame|gripp intern/i.test(p.company || '')).length;
              return zonderCv > 0 ? (
                <Tooltip title="Projecten in deze selectie zonder Contractvorm. Bulk op 'Los project' zetten kan via Admin (contractvorm-vullen); retainers daarna handmatig.">
                  <Chip label={`${zonderCv} nog contractvorm`} sx={{ fontWeight: 700, bgcolor: '#fef3c7', color: '#92400e', cursor: 'help' }} />
                </Tooltip>
              ) : null;
            })()}
            {untyped.length > 0 && (
              <Tooltip title="Projecten uit 2026 zonder ingevulde vrije velden — vul ze aan in Gripp voor een completer beeld.">
                <Chip label={`${untyped.length} zonder typering (2026)`} sx={{ fontWeight: 700, bgcolor: '#fef3c7', color: '#92400e', cursor: 'help' }} />
              </Tooltip>
            )}
          </Stack>

          {/* Nieuwe koers: hoofdpijler-aandeel + niveau-KPI's — niet bij Very Video (bureauwerk is per definitie geen advies-eerst) */}
          {segment !== 'exclusive' && (
          <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderColor: '#c7d2fe', bgcolor: '#fafaff' }}>
            <Typography sx={{ fontWeight: 800, fontSize: 15, mb: 0.25 }}>🧭 Nieuwe koers — van projectleverancier naar strategisch adviseur</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Hoofdpijler-aandeel en opdracht-niveau moeten groeien (herpositionering mei 2026). Vergelijking: laatste 90 dagen t.o.v. de hele periode.
            </Typography>
            {(() => {
              const omzetVan = (list) => list.reduce((s, p) => s + (p.amount_excl_vat || 0), 0);
              const kpi = (list) => {
                const omzet = omzetVan(list);
                const hoofd = omzetVan(list.filter((p) => pijlerVoorDoel(p.doel) !== 'overig'));
                const groot = omzetVan(list.filter((p) => ['L', 'XL'].includes((p.omvang || '').toUpperCase())));
                const tender = omzetVan(list.filter((p) => p.tender_klant));
                const advies = omzetVan(list.filter(isAdvies));
                const doorlopend = omzetVan(list.filter((p) => p.doorlopend));
                return {
                  hoofdAandeel: pct(hoofd, omzet),
                  grootAandeel: pct(groot, omzet),
                  tenderAandeel: pct(tender, omzet),
                  adviesAandeel: pct(advies, omzet),
                  adviesOmzet: advies,
                  diagnoses: list.filter(isDiagnose).length,
                  doorlopendAandeel: pct(doorlopend, omzet),
                  gemWaarde: list.length ? omzet / list.length : 0,
                  mediaanWaarde: mediaan(list.map((p) => p.amount_excl_vat || 0)),
                };
              };
              const heel = kpi(typed);
              const laatste = kpi(recent);
              const genoegRecent = recent.length >= MIN_RECENT;
              const Delta = ({ nu, was, suffix = '%' }) => {
                const d = Math.round(nu - was);
                if (!genoegRecent) return null;
                return (
                  <Box component="span" sx={{ ml: 0.75, fontSize: 12, fontWeight: 800, color: d >= 0 ? '#15803d' : '#b91c1c' }}>
                    {d >= 0 ? '↑' : '↓'}{Math.abs(d)}{suffix}
                  </Box>
                );
              };
              return (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)', xl: 'repeat(6, 1fr)' }, gap: 2 }}>
                  <Box>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Omzet uit hoofdpijlers</Typography>
                    <Typography sx={{ fontSize: 26, fontWeight: 800 }}>
                      {heel.hoofdAandeel}%<Delta nu={laatste.hoofdAandeel} was={heel.hoofdAandeel} />
                    </Typography>
                    <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                      {PIJLERS.map((p) => {
                        const share = pct(omzetVan(typed.filter((x) => pijlerVoorDoel(x.doel) === p.key)), omzetVan(typed));
                        return (
                          <Typography key={p.key} sx={{ fontSize: 12, color: '#556071' }}>
                            <Box component="span" sx={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', bgcolor: p.kleur, mr: 0.75 }} />
                            {p.label}: <b>{share}%</b>
                          </Typography>
                        );
                      })}
                    </Stack>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Gemiddelde opdrachtwaarde</Typography>
                    <Typography sx={{ fontSize: 26, fontWeight: 800 }}>
                      {euro(heel.gemWaarde)}
                      <Box component="span" sx={{ ml: 0.75, fontSize: 12, fontWeight: 600, color: '#64748b' }}>mediaan {euro(heel.mediaanWaarde)}</Box>
                      {genoegRecent && (
                        <Box component="span" sx={{ ml: 0.75, fontSize: 12, fontWeight: 800, color: laatste.gemWaarde >= heel.gemWaarde ? '#15803d' : '#b91c1c' }}>
                          {laatste.gemWaarde >= heel.gemWaarde ? '↑' : '↓'} {euro(laatste.gemWaarde)} (90d)
                        </Box>
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Koers: omhoog — weg van het niveau dat een junior inkoper zelfstandig afhandelt.</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Omzet uit L/XL-opdrachten</Typography>
                    <Typography sx={{ fontSize: 26, fontWeight: 800 }}>
                      {heel.grootAandeel}%<Delta nu={laatste.grootAandeel} was={heel.grootAandeel} />
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Koers: omhoog. Retainer-KPI (40% H1 '27) volgt zodra projecten een Contractvorm hebben.
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Tender-omzet (Interne Videopartner)</Typography>
                    <Typography sx={{ fontSize: 26, fontWeight: 800 }}>
                      {heel.tenderAandeel}%<Delta nu={laatste.tenderAandeel} was={heel.tenderAandeel} />
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Per klant: Enexis, OMWB, Provincie Noord-Brabant (instelling ‘tender_klanten’). Bewuste nevenpijler — terugkerende omzet.
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Advies-omzet</Typography>
                    <Typography sx={{ fontSize: 26, fontWeight: 800 }}>
                      {heel.adviesAandeel}%<Delta nu={laatste.adviesAandeel} was={heel.adviesAandeel} />
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {euro(heel.adviesOmzet)} uit Concept & strategie en Diagnose · <b>{heel.diagnoses}</b> diagnose{heel.diagnoses === 1 ? '' : 's'} verkocht. Koers: advies-eerst.
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Doorlopend werk</Typography>
                    <Typography sx={{ fontSize: 26, fontWeight: 800 }}>
                      {heel.doorlopendAandeel}%<Delta nu={laatste.doorlopendAandeel} was={heel.doorlopendAandeel} />
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Retainer of raamcontract, óf journaal / kanaalbeheer / community management — ook als het contract ‘los’ heet. Retainer-KPI 40% H1 ’27.
                    </Typography>
                  </Box>
                </Box>
              );
            })()}
          </Paper>
          )}

          {/* Full Frame Exclusive / Very Video: werk voor reclamebureaus (zusjes = ONLY-groep, los = zelfstandig) */}
          {(() => {
            const omzetVan = (list) => list.reduce((s, p) => s + (p.amount_excl_vat || 0), 0);
            const basisLijst = scopedAlle;
            const ex = basisLijst.filter((p) => p.exclusive);
            const zus = ex.filter((p) => p.exclusive === 'zusje');
            const los = ex.filter((p) => p.exclusive === 'los');
            const tot = omzetVan(basisLijst);
            const cutoff = new Date(Date.now() - 90 * 86400000).toISOString().slice(0, 10);
            const recentAlle = deltaZinvol ? basisLijst.filter((p) => (p.start_date || '') >= cutoff) : [];
            const aandeel = pct(omzetVan(ex), tot);
            const aandeelRecent = recentAlle.length ? pct(omzetVan(recentAlle.filter((p) => p.exclusive)), omzetVan(recentAlle)) : null;
            const perBureau = new Map();
            for (const p of ex) {
              const k = p.exclusive_bureau || p.company;
              const cur = perBureau.get(k) || { naam: k, zusje: p.exclusive === 'zusje', count: 0, omzet: 0 };
              cur.count += 1; cur.omzet += p.amount_excl_vat || 0; perBureau.set(k, cur);
            }
            const bureaus = [...perBureau.values()].sort((a, b) => b.omzet - a.omzet);
            const maxOmzet = bureaus[0]?.omzet || 1;
            const perEind = new Map();
            for (const p of ex) {
              const k = p.eindklant || `(onbekend · ${p.exclusive_bureau || p.company})`;
              const cur = perEind.get(k) || { naam: k, count: 0, omzet: 0, bureaus: new Set() };
              cur.count += 1; cur.omzet += p.amount_excl_vat || 0; cur.bureaus.add(p.exclusive_bureau || p.company); perEind.set(k, cur);
            }
            const eindklanten = [...perEind.values()].sort((a, b) => b.omzet - a.omzet);
            const vvOmzet = omzetVan(ex);
            const topEind = eindklanten.find((e) => !e.naam.startsWith('(onbekend'));
            const onbekendEind = ex.filter((p) => !p.eindklant).length;
            return (
              <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderColor: '#fbcfe8', bgcolor: '#fff7fb' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" useFlexGap>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: 15, mb: 0.25 }}>🎬 Very Video <Box component="span" sx={{ fontWeight: 500, color: '#64748b', fontSize: 12 }}>(Full Frame Exclusive)</Box></Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                      Whitelabel werk voor reclamebureaus. Zusjes = onderdeel van de ONLY-groep, los = zelfstandig bureau. Lijst en indeling: Admin → Applicatiebeheer → Very Video-bureaus.
                    </Typography>
                  </Box>
                  <Chip size="small" label={segment === 'alle' ? 'Filter de mix hieronder via “Very Video” bovenin' : `Mix hieronder: ${segment === 'exclusive' ? 'alleen Very Video' : 'zonder Very Video'}`} sx={{ bgcolor: '#fdf2f8', color: '#9d174d', fontWeight: 600 }} />
                </Stack>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr 2fr 2fr' }, gap: 2 }}>
                  <Box>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Aandeel in de omzet</Typography>
                    <Typography sx={{ fontSize: 26, fontWeight: 800 }}>
                      {aandeel}%
                      {aandeelRecent != null && (
                        <Box component="span" sx={{ ml: 0.75, fontSize: 12, fontWeight: 800, color: aandeelRecent >= aandeel ? '#15803d' : '#b91c1c' }}>
                          {aandeelRecent >= aandeel ? '↑' : '↓'}{Math.abs(aandeelRecent - aandeel)}% (90d)
                        </Box>
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{euro(omzetVan(ex))} · {ex.length} projecten van {basisLijst.length}</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Zusjes (ONLY)</Typography>
                    <Typography sx={{ fontSize: 26, fontWeight: 800 }}>{euro(omzetVan(zus))}</Typography>
                    <Typography variant="caption" color="text.secondary">{pct(omzetVan(zus), tot)}% van alles · {zus.length} projecten</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Losse bureaus</Typography>
                    <Typography sx={{ fontSize: 26, fontWeight: 800 }}>{euro(omzetVan(los))}</Typography>
                    <Typography variant="caption" color="text.secondary">{pct(omzetVan(los), tot)}% van alles · {los.length} projecten</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5 }}>Per bureau ({periode === 'alles' ? 'hele periode' : periode})</Typography>
                    <Stack spacing={0.5}>
                      {bureaus.slice(0, 10).map((b) => (
                        <Box key={b.naam}>
                          <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                            <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>
                              {b.naam} <Box component="span" sx={{ ml: 0.5, fontSize: 10, fontWeight: 800, color: b.zusje ? '#9d174d' : '#64748b' }}>{b.zusje ? 'zusje' : 'los'}</Box>
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: '#767c7e' }}>{euro(b.omzet)} · {b.count}×</Typography>
                          </Stack>
                          <Box sx={{ height: 5, borderRadius: 3, bgcolor: '#fce7f3', overflow: 'hidden' }}>
                            <Box sx={{ width: `${Math.round((b.omzet / maxOmzet) * 100)}%`, height: '100%', bgcolor: b.zusje ? '#be185d' : '#94a3b8' }} />
                          </Box>
                        </Box>
                      ))}
                      {bureaus.length === 0 && <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>Geen bureau-omzet in deze periode</Typography>}
                    </Stack>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5 }}>
                      Per eindklant {topEind ? `· grootste ${pct(topEind.omzet, vvOmzet)}%` : ''}
                    </Typography>
                    <Stack spacing={0.5}>
                      {eindklanten.slice(0, 10).map((e) => (
                        <Box key={e.naam}>
                          <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: e.naam.startsWith('(onbekend') ? '#94a3b8' : '#2d3335' }}>{e.naam} <Box component="span" sx={{ fontSize: 10, color: '#94a3b8' }}>via {[...e.bureaus].join(', ')}</Box></Typography>
                            <Typography sx={{ fontSize: 12, color: '#767c7e' }}>{euro(e.omzet)} · {e.count}×</Typography>
                          </Stack>
                          <Box sx={{ height: 5, borderRadius: 3, bgcolor: '#fce7f3', overflow: 'hidden' }}>
                            <Box sx={{ width: `${Math.round((e.omzet / (eindklanten[0]?.omzet || 1)) * 100)}%`, height: '100%', bgcolor: e.naam.startsWith('(onbekend') ? '#e2e8f0' : '#9d174d' }} />
                          </Box>
                        </Box>
                      ))}
                      {eindklanten.length === 0 && <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>Geen bureau-omzet in deze periode</Typography>}
                    </Stack>
                    {onbekendEind > 0 && (
                      <Button size="small" sx={{ mt: 1, textTransform: 'none' }} onClick={async () => {
                        const r = await authFetch(`${BACKEND_URL}/Api/gripp/portfolios/eindklant/afleiden`, { method: 'POST' });
                        const d = await r.json().catch(() => ({}));
                        toast(r.ok ? `Eindklant afgeleid voor ${d.afgeleid} projecten — herlaad de pagina.` : (d.error || 'Mislukt'));
                      }}>Eindklant afleiden met AI ({onbekendEind} zonder)</Button>
                    )}
                  </Box>
                </Box>
              </Paper>
            );
          })()}

          {/* 4 dimensie-kaarten */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', xl: '1fr 1fr 1fr' }, gap: 2, mb: 4 }}>
            {DIMENSIONS.map((d) => (
              <DimensionCard key={d.key} dim={d.key} label={d.label} icon={d.icon}
                projects={projects} recent={recent} basis={basis} onRij={(dim, label, value) => setKlantenDialog({ dim, label, value })} />
            ))}
          </Box>

          {/* Trend per maand */}
          <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              <Typography sx={{ fontWeight: 800, fontSize: 15 }}>📈 Mix per maand op startdatum ({basis === 'omzet' ? 'omzet' : 'aantal projecten'})</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <ToggleButtonGroup size="small" exclusive value={chartType} onChange={(_e, v) => v && setChartType(v)}
                  sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 1.25, fontSize: 12, textTransform: 'none', fontWeight: 700 } }}>
                  <ToggleButton value="bar">Staaf</ToggleButton>
                  <ToggleButton value="line">Lijn</ToggleButton>
                </ToggleButtonGroup>
                <ToggleButtonGroup size="small" exclusive value={trendDim} onChange={(_e, v) => { if (v) { setTrendDim(v); setHiddenSeries({}); } }}
                  sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 1.25, fontSize: 12, textTransform: 'none', fontWeight: 700 } }}>
                  {DIMENSIONS.map((d) => <ToggleButton key={d.key} value={d.key}>{d.label}</ToggleButton>)}
                </ToggleButtonGroup>
              </Stack>
            </Stack>
            <ResponsiveContainer width="100%" height={340}>
              {chartType === 'bar' ? (
                <BarChart data={trendData.rows}>
                  <XAxis dataKey="maand" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => (basis === 'omzet' ? `€${Math.round(v / 1000)}k` : v)} />
                  <ReTooltip formatter={(v) => (basis === 'omzet' ? euro(v) : v)} />
                  <Legend wrapperStyle={{ fontSize: 12, cursor: 'pointer' }}
                    onClick={(e) => toggleSeries(e.dataKey ?? e.value)}
                    formatter={(value) => <span style={{ textDecoration: hiddenSeries[value] ? 'line-through' : 'none', opacity: hiddenSeries[value] ? 0.45 : 1 }}>{value}</span>} />
                  {trendData.values.map((v, i) => (
                    <Bar key={v} dataKey={v} stackId="mix" hide={!!hiddenSeries[v]} fill={v === 'Onbekend' ? '#cbd5e1' : PALETTE[i % PALETTE.length]} />
                  ))}
                </BarChart>
              ) : (
                <LineChart data={trendData.rows}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef0f2" />
                  <XAxis dataKey="maand" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => (basis === 'omzet' ? `€${Math.round(v / 1000)}k` : v)} />
                  <ReTooltip formatter={(v) => (basis === 'omzet' ? euro(v) : v)} />
                  <Legend wrapperStyle={{ fontSize: 12, cursor: 'pointer' }}
                    onClick={(e) => toggleSeries(e.dataKey ?? e.value)}
                    formatter={(value) => <span style={{ textDecoration: hiddenSeries[value] ? 'line-through' : 'none', opacity: hiddenSeries[value] ? 0.45 : 1 }}>{value}</span>} />
                  {trendData.values.map((v, i) => (
                    <Line key={v} type="monotone" dataKey={v} hide={!!hiddenSeries[v]} dot={false} strokeWidth={2}
                      stroke={v === 'Onbekend' ? '#cbd5e1' : PALETTE[i % PALETTE.length]} />
                  ))}
                </LineChart>
              )}
            </ResponsiveContainer>
            <Typography variant="caption" color="text.secondary">
              Klik op een legenda-item om die reeks aan/uit te zetten. Maanden vóór de invoering van de vrije velden tonen weinig/geen data.
            </Typography>
          </Paper>

          {/* Data-hygiëne: recente projecten zonder typering */}
          {untyped.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2.5 }}>
              <Typography sx={{ fontWeight: 800, fontSize: 15, mb: 1 }}>🏷️ Nog typeren in Gripp ({untyped.length})</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                2026-projecten zonder Doel/Projecttype/Dienst/Omvang — aanvullen onder “Vrije velden” op het project.
              </Typography>
              <Stack spacing={0.5}>
                {untyped.slice(0, 12).map((p) => {
                  const url = buildGrippProjectUrl(p.gripp_id, p.company_id);
                  return (
                    <Typography key={p.gripp_id} sx={{ fontSize: 13 }}>
                      {url ? (
                        <Box component="a" href={url} target="_blank" rel="noopener noreferrer"
                          sx={{ color: '#3b5bdb', textDecoration: 'none', fontWeight: 700, '&:hover': { textDecoration: 'underline' } }}>
                          {p.number} — {p.name} ↗
                        </Box>
                      ) : (
                        <b>{p.number} — {p.name}</b>
                      )}
                      {' '}<Box component="span" sx={{ color: '#94a3b8' }}>({p.company || 'geen klant'})</Box>
                    </Typography>
                  );
                })}
                {untyped.length > 12 && <Typography variant="caption" color="text.secondary">… en {untyped.length - 12} meer</Typography>}
              </Stack>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
}
